import React from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Check, X as XIcon, ChevronDown, ChevronUp, Clock,
  AlertOctagon, Sparkles, ShieldCheck, Zap, Radar, ExternalLink, RotateCcw,
} from "lucide-react";

/**
 * Convert a Bluesky AT-URI (at://did:plc:xxxx/app.bsky.feed.post/rkey) plus
 * the author handle into a bsky.app web URL so it's clickable. Returns null
 * if we can't build one.
 */
function blueskyWebUrl(reply) {
  if (!reply || reply.platform !== "bluesky") return null;
  const uri = reply.native_uri || "";
  const handle = (reply.author_handle || "").replace(/^@/, "");
  if (!uri || !handle) return null;
  const m = uri.match(/\/app\.bsky\.feed\.post\/([a-z0-9]+)$/i);
  if (!m) return null;
  return `https://bsky.app/profile/${handle}/post/${m[1]}`;
}

/**
 * Evening Review — every pending comment, one card each. Each Approve or
 * Skip commits to the backend immediately (no "submit batch" step), so
 * decisions can't be lost by closing the tab or clicking away. Approved
 * replies move straight into the "Queued for tomorrow" block above.
 * Skipped replies disappear from the list. Both have a 20-second undo.
 */

const PLATFORM_LABEL = {
  bluesky: "Bluesky",
  twitter: "X / Twitter",
  website: "Website",
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
};

const CLASS_META = {
  clean: { label: "Clean", color: "var(--elle-moss)" },
  ambiguous: { label: "Flagged · review carefully", color: "var(--elle-amber)" },
  tier3: { label: "Tier-3 · plot probe", color: "var(--elle-oxblood-bright)" },
};

function formatTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB",
    { day:"numeric", month:"short", hour:"2-digit", minute:"2-digit" }); }
  catch { return iso; }
}

export default function EveningReview() {
  const [items, setItems] = React.useState(null);
  const [counts, setCounts] = React.useState({ pending: 0, scheduled: 0, sent_today: 0, daily_cap: 10 });
  // drafts[reply_id] = current textarea content, independent of server state.
  const [drafts, setDrafts] = React.useState({});
  const [busyDraft, setBusyDraft] = React.useState({});
  const [busyCommit, setBusyCommit] = React.useState({});
  const [expanded, setExpanded] = React.useState({});
  const [queued, setQueued] = React.useState([]);
  const [scanning, setScanning] = React.useState(false);
  const [bursting, setBursting] = React.useState(false);
  // Locally-decided cards: { [reply_id]: { action: "approve"|"skip", content?, until } }
  // While `until > Date.now()` we show an Undo card in-place instead of removing.
  const [decided, setDecided] = React.useState({});

  const runProactiveScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/engagement/proactive/scan");
      if (data.paused) {
        toast.warning("Emergency pause is on. Scan halted.");
      } else if (!data.created) {
        toast.info("Nothing new worth engaging with right now. Try again tomorrow.");
      } else {
        toast.success(`Drafted ${data.created} proactive opener${data.created === 1 ? "" : "s"}. Review below.`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed.");
    } finally {
      setScanning(false);
    }
  };

  const load = async () => {
    try {
      const [r, q] = await Promise.all([
        api.get("/engagement/review"),
        api.get("/engagement/queued"),
      ]);
      setItems(r.data.items);
      setCounts(r.data.counts);
      setQueued(q.data);
      setDrafts(prev => {
        const next = { ...prev };
        for (const x of r.data.items) {
          if (next[x.id] === undefined) {
            next[x.id] = x.elle_response || x.agent_draft || "";
          }
        }
        return next;
      });
    } catch {
      toast.error("Couldn't load evening review.");
    }
  };
  React.useEffect(() => { load(); }, []);

  // Clear local `decided` marks once their undo window has elapsed. When an
  // entry expires, also drop the reply from `items` so the row disappears
  // permanently instead of re-rendering as a fresh "undecided" card.
  React.useEffect(() => {
    const t = setInterval(() => {
      setDecided(prev => {
        const now = Date.now();
        const next = {};
        const expired = [];
        for (const [k, v] of Object.entries(prev)) {
          if (v.until > now) next[k] = v;
          else expired.push(k);
        }
        if (expired.length) {
          setItems(its => (its ? its.filter(r => !expired.includes(r.id)) : its));
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const draftReply = async (reply) => {
    setBusyDraft(b => ({ ...b, [reply.id]: true }));
    try {
      const { data } = await api.post("/replies/draft", { reply_id: reply.id });
      setDrafts(d => ({ ...d, [reply.id]: data.draft }));
      setExpanded(e => ({ ...e, [reply.id]: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed.");
    } finally {
      setBusyDraft(b => ({ ...b, [reply.id]: false }));
    }
  };

  const setContent = (id, content) =>
    setDrafts(d => ({ ...d, [id]: content }));

  const commit = async (reply, action) => {
    const content = action === "approve" ? (drafts[reply.id] || "").trim() : null;
    if (action === "approve" && content.length < 2) {
      toast.error("Write or draft a reply before approving.");
      return;
    }
    setBusyCommit(b => ({ ...b, [reply.id]: true }));
    try {
      const { data } = await api.post("/engagement/batch", {
        items: [{ reply_id: reply.id, action, content }],
      });
      const lintFail = (data.lint_failures || [])[0];
      if (lintFail) {
        toast.warning(
          `Content linter rejected this one: ${lintFail.error || "hard limit violation"}.`,
          { duration: 8000 }
        );
        return;
      }
      if (action === "approve") {
        toast.success("Queued for tomorrow's burst. Undo within 20s.", { duration: 5000 });
      } else {
        toast.success("Skipped. Undo within 20s.", { duration: 5000 });
      }
      setDecided(d => ({
        ...d,
        [reply.id]: { action, content, until: Date.now() + 20000 },
      }));
      // Re-fetch counts + queued so the top block updates immediately.
      try {
        const [cRes, qRes] = await Promise.all([
          api.get("/engagement/counts"),
          api.get("/engagement/queued"),
        ]);
        setCounts(cRes.data);
        setQueued(qRes.data);
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't commit decision.");
    } finally {
      setBusyCommit(b => ({ ...b, [reply.id]: false }));
    }
  };

  const undo = async (reply_id) => {
    setBusyCommit(b => ({ ...b, [reply_id]: true }));
    try {
      await api.post(`/engagement/reply/${reply_id}/reopen`);
      toast.success("Undone. Back in Waiting.");
      setDecided(d => {
        const next = { ...d };
        delete next[reply_id];
        return next;
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't undo.");
    } finally {
      setBusyCommit(b => ({ ...b, [reply_id]: false }));
    }
  };

  const unschedule = async (reply_id) => {
    if (!window.confirm("Pull this reply back out of tomorrow's queue?")) return;
    try {
      await api.delete(`/engagement/queued/${reply_id}`);
      toast.success("Pulled back. It's in Waiting again.");
      await load();
    } catch {
      toast.error("Couldn't unschedule.");
    }
  };

  const runNow = async () => {
    if (!window.confirm(`Send all ${queued.length} queued replies now, instead of waiting for the morning burst?`)) return;
    setBursting(true);
    try {
      const { data } = await api.post("/engagement/run-burst-now");
      toast.success(`Sent ${data.sent || 0}. ${data.failed ? `${data.failed} failed.` : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Burst failed.");
    } finally {
      setBursting(false);
    }
  };

  const capHeadroom = (counts.daily_cap || 10) - (counts.scheduled + counts.sent_today);
  const atCap = capHeadroom <= 0;

  if (items === null) {
    return <p className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-dim)" }}>
      Compiling the evening review…
    </p>;
  }

  // "Still to decide" = items that aren't in our local decided map (we hide
  // those for 20s as an undo card).
  const visibleItems = items.filter(r => !decided[r.id] || decided[r.id].until > Date.now());

  return (
    <div className="max-w-4xl" data-testid="evening-review-page">
      <div className="overline">Evening review</div>
      <h1 className="font-display text-5xl mt-2" style={{ color: "var(--elle-ink)" }}>
        Your fifteen minutes.
      </h1>
      <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
        Every pending comment with a draft reply. Approve or Skip each one —
        <strong style={{ color: "var(--elle-ink)" }}> decisions commit immediately</strong>.
        Approved replies go into tomorrow&rsquo;s 08:00–10:00 UTC burst, spaced with natural jitter.
        Skipped ones vanish. You have 20 seconds to undo either.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3"
           data-testid="proactive-controls">
        <Button onClick={runProactiveScan}
                disabled={scanning}
                variant="outline"
                className="btn-ghost rounded-sm"
                data-testid="run-proactive-scan">
          <Radar size={14} className="mr-2"/>
          {scanning ? "Scanning…" : "Look for posts I could engage with"}
        </Button>
        <span className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
          Runs automatically every morning at 07:00 UTC. Click to refresh now.
        </span>
      </div>

      {/* Counts strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="review-counts">
        <div className="elle-surface p-4 rounded-sm">
          <div className="overline">Waiting</div>
          <div className="font-display text-4xl mt-1"
               style={{ color: "var(--elle-ink)" }}>{counts.pending}</div>
        </div>
        <div className="elle-surface p-4 rounded-sm">
          <div className="overline">Queued for tomorrow</div>
          <div className="font-display text-4xl mt-1"
               style={{ color: "var(--elle-amber)" }}>{counts.scheduled}</div>
        </div>
        <div className="elle-surface p-4 rounded-sm">
          <div className="overline">Sent today</div>
          <div className="font-display text-4xl mt-1"
               style={{ color: "var(--elle-moss)" }}>{counts.sent_today}</div>
        </div>
        <div className="elle-surface p-4 rounded-sm">
          <div className="overline">Daily cap</div>
          <div className="font-display text-4xl mt-1"
               style={{ color: atCap ? "var(--elle-oxblood)" : "var(--elle-ink)" }}>
            {counts.daily_cap}
          </div>
          <div className="text-xs font-mono-elle mt-1" style={{ color: "var(--elle-ink-faint)" }}>
            {atCap ? "at cap" : `${capHeadroom} left`}
          </div>
        </div>
      </div>

      {atCap && (
        <div className="mt-4 p-3 rounded-sm flex items-start gap-2"
             data-testid="cap-warning"
             style={{ background: "rgba(163,50,50,0.08)", border: "1px solid var(--elle-oxblood)" }}>
          <AlertOctagon size={14} style={{ color: "var(--elle-oxblood-bright)" }} className="mt-0.5"/>
          <div className="text-xs" style={{ color: "var(--elle-ink-dim)" }}>
            Daily cap reached. New approvals won&rsquo;t queue until tomorrow morning&rsquo;s burst empties the pipe.
            Skips still work.
          </div>
        </div>
      )}

      {/* Queued-for-tomorrow preview */}
      {queued.length > 0 && (
        <div className="mt-8 elle-surface-2 rounded-sm p-5"
             data-testid="queued-block"
             style={{ borderLeft: "3px solid var(--elle-amber)" }}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="overline flex items-center gap-2">
                <Clock size={12}/> Already queued for tomorrow morning
              </div>
              <div className="font-display text-2xl mt-1" style={{ color: "var(--elle-ink)" }}>
                {queued.length} reply/replies waiting
              </div>
            </div>
            <Button onClick={runNow} disabled={bursting}
                    variant="outline" className="btn-ghost rounded-sm"
                    data-testid="run-burst-now">
              <Zap size={14} className="mr-2"/>Send now instead
            </Button>
          </div>
          <div className="mt-4 space-y-2">
            {queued.map(q => (
              <div key={q.id} className="flex items-start justify-between gap-3 text-sm py-2 border-t"
                   style={{ borderColor: "var(--elle-line)" }}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
                    → {q.author_handle} on {PLATFORM_LABEL[q.platform] || q.platform} · scheduled {formatTime(q.scheduled_for)}
                  </div>
                  <div className="mt-1 italic" style={{ color: "var(--elle-ink)" }}>
                    &ldquo;{(q.elle_response || "").slice(0, 200)}{(q.elle_response || "").length > 200 ? "…" : ""}&rdquo;
                  </div>
                </div>
                <button onClick={() => unschedule(q.id)}
                        className="text-xs font-mono-elle underline hover:text-[var(--elle-oxblood-bright)]"
                        data-testid={`unschedule-${q.id}`}
                        style={{ color: "var(--elle-ink-faint)" }}>
                  pull back
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="font-display text-3xl mt-10 mb-4" style={{ color: "var(--elle-ink)" }}>
        Comments waiting
      </h2>

      {visibleItems.length === 0 ? (
        <div className="elle-surface p-10 rounded-sm text-center"
             data-testid="review-empty">
          <ShieldCheck size={32} className="mx-auto mb-3" style={{ color: "var(--elle-moss)" }}/>
          <p className="font-display text-2xl" style={{ color: "var(--elle-ink)" }}>
            Quiet evening. Nothing waiting.
          </p>
          <p className="text-sm mt-2" style={{ color: "var(--elle-ink-dim)" }}>
            Scheduled replies for tomorrow: {counts.scheduled}.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleItems.map(r => {
            const cls = CLASS_META[r.classification] || CLASS_META.clean;
            const isOpen = expanded[r.id] !== false;
            const d = decided[r.id];
            const busy = !!busyCommit[r.id];

            // Undo card — replaces the normal card for 20s after commit.
            if (d) {
              const remaining = Math.max(0, Math.ceil((d.until - Date.now()) / 1000));
              const isApprove = d.action === "approve";
              return (
                <div key={r.id}
                     className="elle-surface rounded-sm p-5 flex items-center justify-between gap-4 flex-wrap"
                     data-testid={`decided-${r.id}`}
                     style={{
                       borderLeft: isApprove ? "3px solid var(--elle-moss)" : "3px solid var(--elle-ink-faint)",
                       opacity: 0.85,
                     }}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
                      {isApprove ? (
                        <><Check size={11} className="inline mr-1" style={{ color: "var(--elle-moss)" }}/>
                          Queued for tomorrow · {PLATFORM_LABEL[r.platform] || r.platform} · {r.author_handle}</>
                      ) : (
                        <><XIcon size={11} className="inline mr-1"/>
                          Skipped · {PLATFORM_LABEL[r.platform] || r.platform} · {r.author_handle}</>
                      )}
                    </div>
                    {isApprove && d.content && (
                      <div className="italic text-sm mt-1 truncate" style={{ color: "var(--elle-ink-dim)" }}>
                        &ldquo;{d.content.slice(0, 160)}{d.content.length > 160 ? "…" : ""}&rdquo;
                      </div>
                    )}
                  </div>
                  <button onClick={() => undo(r.id)}
                          disabled={busy}
                          data-testid={`undo-${r.id}`}
                          className="text-xs font-mono-elle px-3 py-1.5 rounded-sm hover:text-[var(--elle-amber)] transition-colors inline-flex items-center gap-1.5"
                          style={{ border: "1px solid var(--elle-border)", color: "var(--elle-ink-dim)" }}>
                    <RotateCcw size={11}/> Undo ({remaining}s)
                  </button>
                </div>
              );
            }

            return (
              <div key={r.id}
                   className="elle-surface rounded-sm transition-all"
                   data-testid={`review-item-${r.id}`}
                   style={{ borderLeft: "3px solid transparent" }}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 text-xs font-mono-elle flex-wrap">
                        <span style={{ color: "var(--elle-amber)" }}>
                          {PLATFORM_LABEL[r.platform] || r.platform}
                        </span>
                        {r.kind === "announcement" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm"
                                data-testid={`announcement-badge-${r.id}`}
                                style={{ background: "rgba(61,90,64,0.18)",
                                         border: "1px solid var(--elle-moss)",
                                         color: "var(--elle-moss)" }}>
                            <Radar size={10}/> announcement
                          </span>
                        ) : r.proactive ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm"
                                data-testid={`proactive-badge-${r.id}`}
                                style={{ background: "rgba(197,160,89,0.15)",
                                         border: "1px solid var(--elle-amber)",
                                         color: "var(--elle-amber)" }}>
                            <Radar size={10}/> proactive
                          </span>
                        ) : null}
                        <span style={{ color: "var(--elle-ink-dim)" }}>
                          {r.kind === "announcement" ? (
                            <>
                              top-level post
                              {r.announcement_issue_slug ? (
                                <span style={{ color: "var(--elle-ink-faint)" }}> · newsletter {r.announcement_issue_slug}</span>
                              ) : null}
                            </>
                          ) : (
                            <>
                              {r.proactive ? "post by " : "from "}
                              <strong style={{ color: "var(--elle-ink)" }}>{r.author_handle}</strong>
                              {r.source_hashtag && r.proactive ? (
                                <span style={{ color: "var(--elle-ink-faint)" }}> · via {r.source_hashtag}</span>
                              ) : null}
                            </>
                          )}
                        </span>
                        <span style={{ color: cls.color }}>· {cls.label}</span>
                        <span style={{ color: "var(--elle-ink-faint)" }}>· {formatTime(r.received_at)}</span>
                      </div>
                      <div className="mt-3 text-base leading-relaxed"
                           style={{ color: "var(--elle-ink)" }}
                           data-testid={`review-comment-${r.id}`}>
                        {r.kind === "announcement" ? (
                          <span className="italic" style={{ color: "var(--elle-ink-dim)" }}>
                            About to post:
                            <br/>
                          </span>
                        ) : r.proactive ? (
                          <span className="italic" style={{ color: "var(--elle-ink-dim)" }}>
                            They wrote:
                            <br/>
                          </span>
                        ) : null}
                        {r.kind === "announcement" ? (r.agent_draft || r.elle_response) : r.sanitised_preview}
                      </div>
                      {(() => {
                        const url = blueskyWebUrl(r);
                        return url ? (
                          <a href={url} target="_blank" rel="noopener noreferrer"
                             data-testid={`view-original-${r.id}`}
                             className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono-elle hover:text-[var(--elle-amber)] transition-colors"
                             style={{ color: "var(--elle-ink-dim)" }}>
                            <ExternalLink size={11}/>
                            View original post on Bluesky (new tab)
                          </a>
                        ) : null;
                      })()}
                    </div>
                    <button onClick={() => setExpanded(e => ({ ...e, [r.id]: !isOpen }))}
                            className="p-2 rounded-sm hover:bg-white/5"
                            aria-label={isOpen ? "Collapse" : "Expand"}>
                      {isOpen ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                    </button>
                  </div>

                  {isOpen && (
                    <div className="mt-5" data-testid={`review-draft-area-${r.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="overline">Your reply</div>
                        {!(drafts[r.id] || "").trim() && (
                          <Button size="sm" variant="outline" className="btn-ghost rounded-sm"
                                  disabled={busyDraft[r.id]}
                                  onClick={() => draftReply(r)}
                                  data-testid={`review-draft-btn-${r.id}`}>
                            <Sparkles size={12} className="mr-1"/>
                            {busyDraft[r.id] ? "Drafting…" : "Let the agent draft"}
                          </Button>
                        )}
                      </div>
                      <Textarea
                        value={drafts[r.id] || ""}
                        onChange={e => setContent(r.id, e.target.value)}
                        rows={3}
                        placeholder="Write your reply, or click 'Let the agent draft' above."
                        data-testid={`review-textarea-${r.id}`}
                        className="font-mono-elle text-sm"/>

                      <div className="mt-4 flex flex-wrap gap-2 items-center">
                        <Button onClick={() => commit(r, "approve")}
                                disabled={busy || atCap}
                                className="btn-amber rounded-sm"
                                data-testid={`review-approve-${r.id}`}
                                title={atCap ? "Daily cap reached" : "Approve & queue for tomorrow"}>
                          <Check size={14} className="mr-1"/>
                          {busy ? "Queuing…" : "Approve & queue for tomorrow"}
                        </Button>
                        <Button onClick={() => commit(r, "skip")}
                                disabled={busy}
                                className="btn-ghost rounded-sm"
                                variant="outline"
                                data-testid={`review-skip-${r.id}`}>
                          <XIcon size={14} className="mr-1"/>
                          {busy ? "Skipping…" : "Skip (remove)"}
                        </Button>
                        <span className="text-xs font-mono-elle ml-2"
                              style={{ color: "var(--elle-ink-faint)" }}>
                          Commits immediately. 20s undo.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
